import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { appointmentAPI } from '../../lib/api';
import {
  VideoCamera,
  VideoCameraSlash,
  Microphone,
  MicrophoneSlash,
  Phone,
  User,
  Users,
  Clock,
  CheckCircle,
  HourglassHigh,
  Gear,
  Monitor,
  ChatCircle,
  List
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function TelehealthRoomPage() {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [inCall, setInCall] = useState(false);
  const [waitingPatients, setWaitingPatients] = useState([]);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [showPatientList, setShowPatientList] = useState(true);

  // Initialize camera
  useEffect(() => {
    const initCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (error) {
        console.error('Error accessing camera:', error);
        toast.error('Could not access camera/microphone');
      }
    };
    
    initCamera();
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Load today's appointments (waiting patients)
  useEffect(() => {
    const loadTodaysAppointments = async () => {
      try {
        const res = await appointmentAPI.getAll();
        const today = new Date().toDateString();
        const todaysAppts = res.data.filter(apt => {
          const aptDate = new Date(apt.scheduled_time).toDateString();
          return aptDate === today && ['scheduled', 'checked_in'].includes(apt.status);
        }).sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
        
        setWaitingPatients(todaysAppts);
      } catch (error) {
        console.error('Error loading appointments:', error);
      }
    };
    
    loadTodaysAppointments();
    // Poll every 30 seconds
    const interval = setInterval(loadTodaysAppointments, 30000);
    return () => clearInterval(interval);
  }, []);

  // Toggle camera
  const toggleCamera = () => {
    if (stream) {
      stream.getVideoTracks().forEach(track => {
        track.enabled = !cameraOn;
      });
      setCameraOn(!cameraOn);
    }
  };

  // Toggle microphone
  const toggleMic = () => {
    if (stream) {
      stream.getAudioTracks().forEach(track => {
        track.enabled = !micOn;
      });
      setMicOn(!micOn);
    }
  };

  // Start call with patient
  const startCallWithPatient = (patient) => {
    setCurrentPatient(patient);
    setInCall(true);
    toast.success(`Starting call with ${patient.patient_name}`);
  };

  // End call
  const endCall = () => {
    setInCall(false);
    setCurrentPatient(null);
    toast.info('Call ended');
  };

  // Format time
  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="h-[calc(100vh-80px)] flex">
      {/* Main Video Area */}
      <div className="flex-1 bg-slate-900 relative flex flex-col">
        {/* Video Display */}
        <div className="flex-1 relative">
          {/* Main Video (Patient would appear here in real implementation) */}
          <div className="absolute inset-0 flex items-center justify-center">
            {inCall && currentPatient ? (
              <div className="text-center text-white">
                <div className="w-32 h-32 bg-slate-700 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <User size={64} className="text-slate-500" />
                </div>
                <p className="text-xl font-semibold">{currentPatient.patient_name}</p>
                <p className="text-slate-400">Connecting...</p>
              </div>
            ) : (
              <div className="text-center text-slate-500">
                <VideoCamera size={64} className="mx-auto mb-4" />
                <p className="text-lg">No active call</p>
                <p className="text-sm">Select a patient from the waiting list to start</p>
              </div>
            )}
          </div>

          {/* Self Video (Picture-in-Picture) */}
          <div className="absolute bottom-4 right-4 w-48 h-36 bg-slate-800 rounded-xl overflow-hidden shadow-lg border-2 border-slate-700">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className={`w-full h-full object-cover ${!cameraOn ? 'hidden' : ''}`}
            />
            {!cameraOn && (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <VideoCameraSlash size={32} className="mx-auto" />
                  <p className="text-xs mt-1">Camera Off</p>
                </div>
              </div>
            )}
            <div className="absolute bottom-2 left-2 text-xs text-white bg-black/50 px-2 py-1 rounded">
              {user?.first_name} {user?.last_name} (You)
            </div>
          </div>

          {/* Call Status */}
          {inCall && (
            <div className="absolute top-4 left-4 bg-black/50 text-white px-4 py-2 rounded-lg flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm">In Call with {currentPatient?.patient_name}</span>
            </div>
          )}
        </div>

        {/* Video Controls */}
        <div className="bg-slate-800 p-4">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={toggleMic}
              className={`p-4 rounded-full ${micOn ? 'bg-slate-700 hover:bg-slate-600' : 'bg-red-600 hover:bg-red-700'} text-white transition-colors`}
              title={micOn ? 'Mute' : 'Unmute'}
            >
              {micOn ? <Microphone size={24} /> : <MicrophoneSlash size={24} />}
            </button>
            
            <button
              onClick={toggleCamera}
              className={`p-4 rounded-full ${cameraOn ? 'bg-slate-700 hover:bg-slate-600' : 'bg-red-600 hover:bg-red-700'} text-white transition-colors`}
              title={cameraOn ? 'Turn off camera' : 'Turn on camera'}
            >
              {cameraOn ? <VideoCamera size={24} /> : <VideoCameraSlash size={24} />}
            </button>

            {inCall && (
              <button
                onClick={endCall}
                className="p-4 rounded-full bg-red-600 hover:bg-red-700 text-white transition-colors"
                title="End Call"
              >
                <Phone size={24} className="rotate-[135deg]" />
              </button>
            )}

            <button
              className="p-4 rounded-full bg-slate-700 hover:bg-slate-600 text-white transition-colors"
              title="Share Screen"
            >
              <Monitor size={24} />
            </button>

            <button
              className="p-4 rounded-full bg-slate-700 hover:bg-slate-600 text-white transition-colors"
              title="Chat"
            >
              <ChatCircle size={24} />
            </button>

            <button
              onClick={() => setShowPatientList(!showPatientList)}
              className={`p-4 rounded-full ${showPatientList ? 'bg-teal-600' : 'bg-slate-700'} hover:bg-teal-700 text-white transition-colors lg:hidden`}
              title="Patient List"
            >
              <List size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Patient Waiting List Sidebar */}
      <div className={`w-80 bg-white border-l border-slate-200 flex flex-col ${showPatientList ? '' : 'hidden lg:flex'}`}>
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Users size={20} className="text-teal-600" />
            Today's Patients
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {waitingPatients.length} patient(s) scheduled
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {waitingPatients.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              <HourglassHigh size={32} className="mx-auto mb-2" />
              <p className="text-sm">No patients waiting</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {waitingPatients.map((patient) => (
                <div
                  key={patient.id}
                  className={`p-4 hover:bg-slate-50 transition-colors ${
                    currentPatient?.id === patient.id ? 'bg-teal-50 border-l-4 border-teal-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-slate-900">{patient.patient_name}</p>
                      <p className="text-sm text-slate-500 capitalize">
                        {patient.appointment_type?.replace(/_/g, ' ')}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      patient.status === 'checked_in' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {patient.status === 'checked_in' ? 'Ready' : 'Scheduled'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                    <Clock size={14} />
                    {formatTime(patient.scheduled_time)}
                  </div>

                  {currentPatient?.id === patient.id ? (
                    <div className="flex items-center gap-2 text-teal-600 text-sm">
                      <CheckCircle size={16} />
                      In Call
                    </div>
                  ) : (
                    <button
                      onClick={() => startCallWithPatient(patient)}
                      disabled={inCall}
                      className={`w-full py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${
                        inCall 
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                          : 'bg-teal-600 text-white hover:bg-teal-700'
                      }`}
                    >
                      <VideoCamera size={16} />
                      {inCall ? 'In Another Call' : 'Start Call'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-teal-600">{waitingPatients.filter(p => p.status === 'checked_in').length}</p>
              <p className="text-xs text-slate-500">Ready</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{waitingPatients.filter(p => p.status === 'scheduled').length}</p>
              <p className="text-xs text-slate-500">Waiting</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
